import React, { useState, useEffect, useRef } from 'react';
import { Quote } from 'lucide-react';

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const testimonials = [
    {
      name: 'Maria L.',
      role: 'Fashion Designer',
      image: '/static/images/Testimonial/Testimonial2.jpeg',
      quote: '"Serenoly is my sanctuary! From the moment I step in, the professionals make me feel at ease. Their focus and transformative magic has me coming back! I highly recommend to anyone looking for top-notch care."'
    },
    {
      name: 'Sofia G.',
      role: 'Engineer',
      image: '/static/images/Testimonial/Testimonial1.jpeg',
      quote: '"I booked a therapist to come to my apartment after work and it was seamless — matched in minutes, and my payment was protected the whole time. Best massage I\'ve had, hands down."'
    },
    {
      name: 'James R.',
      role: 'Trainer',
      image: '/static/images/Testimonial/Testimonial3.jpeg',
      quote: '"As someone who travels for work, being able to book a verified therapist to my hotel room through Serenoly is a game changer. Every therapist so far has been professional and on time."'
    }
  ];

  // Create extended array only for mobile view
  const extendedTestimonials = [
    ...testimonials,
    ...testimonials,
    ...testimonials,
    ...testimonials,
    ...testimonials
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is the md breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return; // Only run carousel logic on mobile

    const handleScroll = () => {
      if (!scrollRef.current || isDragging) return;

      const scrollContainer = scrollRef.current;
      const scrollPosition = scrollContainer.scrollLeft;
      const cardWidth = scrollContainer.offsetWidth;
      const totalWidth = scrollContainer.scrollWidth;
      
      const currentIndex = Math.round(scrollPosition / cardWidth) % testimonials.length;
      setActiveIndex(currentIndex);

      if (scrollPosition >= totalWidth - cardWidth * 3) {
        scrollContainer.scrollLeft = cardWidth * (testimonials.length * 2);
      } else if (scrollPosition <= cardWidth * 2) {
        scrollContainer.scrollLeft = cardWidth * (testimonials.length * 3);
      }
    };

    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      scrollContainer.scrollLeft = scrollContainer.offsetWidth * (testimonials.length * 2);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [testimonials.length, isDragging, isMobile]);

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  const scrollTo = (index) => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.offsetWidth;
      const targetScroll = (index + testimonials.length * 2) * cardWidth;
      scrollRef.current.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  const TestimonialCard = ({ testimonial, isActive = true }) => (
    <div 
      className={`flex flex-col bg-secondary-light/10 backdrop-blur-sm items-center text-center p-4 md:p-8 rounded-lg transition-all duration-300 relative h-full
        ${isActive ? 'scale-100 opacity-100' : 'scale-80 opacity-70'}`}
    >
      <div className="absolute -top-3 md:-top-4 left-1/2 -translate-x-1/2 bg-accent-hard p-1.5 md:p-2 rounded-full">
        <Quote className="w-3 h-3 md:w-4 md:h-4 text-white" />
      </div>

      <div className="relative mb-4 md:mb-6">
        <div className="w-16 h-16 md:w-24 md:h-24 rounded-full overflow-hidden ring-2 md:ring-4 ring-accent-hard/5">
          <img
            src={testimonial.image}
            alt={testimonial.name}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <p className="mb-4 md:mb-6 text-secondary-dark italic text-md leading-relaxed">
        {testimonial.quote}
      </p>

      <div className="mt-auto">
        <h3 className="font-bold text-sm md:text-base mb-0.5 md:mb-1 text-accent-light">
          {testimonial.name}
        </h3>
        <p className="text-xs md:text-sm text-secondary-light">
          {testimonial.role}
        </p>
      </div>
    </div>
  );

  return (
    <section className="py-8 md:py-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white relative overflow-hidden">
      <div className="block absolute inset-0 opacity-20">
        <div className="absolute w-48 md:w-64 h-48 md:h-64 rounded-full bg-accent-hard blur-2xl md:blur-3xl -top-32 -left-32" />
        <div className="absolute w-48 md:w-64 h-48 md:h-64 rounded-full bg-primary blur-2xl md:blur-3xl -bottom-32 -right-32" />
        <div className="absolute w-48 md:w-64 h-48 md:h-64 rounded-full bg-secondary-dark blur-2xl md:blur-3xl -z-10 top-10 -right-32" />
      </div>

      <div className="max-w-6xl mx-auto px-4 relative">
        <div className="text-center mb-8">
          <h2 className="text-xl md:text-3xl font-bold font-body text-secondary inline-block relative">
            Our Clients
            <div className="absolute -bottom-2 left-0 w-full h-0.5 md:h-1 bg-accent-hard transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100" />
          </h2>
        </div>

        {/* Desktop Grid View */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 py-6">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="h-full">
              <TestimonialCard testimonial={testimonial} />
            </div>
          ))}
        </div>

        {/* Mobile Carousel View */}
        <div className="md:hidden">
          <div 
            ref={scrollRef}
            className="flex overflow-x-auto rounded-4xl snap-x snap-mandatory scrollbar-hide py-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {extendedTestimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="flex-none w-full snap-center px-4"
              >
                <TestimonialCard 
                  testimonial={testimonial} 
                  isActive={index % testimonials.length === activeIndex}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 
                  ${index === activeIndex ? 'bg-accent-hard w-4' : 'bg-secondary-light/30'}`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;